import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import AzureBackupCalc from '../components/calculators/AzureBackupCalc'
import { generateBreadcrumbList } from '../utils/schemaHelpers'
import { Breadcrumbs } from '../components/shared/Breadcrumbs'

export default function AzureBackupPage() {
  return (
    <div className="px-6 py-6 w-full">
      <Helmet>
        <title>Azure Backup Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure Backup costs for UK South. VM, SQL Server, and Azure Files workloads. See why vault storage typically costs 3–5× the per-instance fee. GBP pricing." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/azure-backup" />
        <meta property="og:title" content="Azure Backup Calculator UK" />
        <meta property="og:description" content="Free independent Azure Backup pricing for UK South. Instance fees + vault storage in GBP. The storage line is usually the surprise." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/azure-backup" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to estimate Azure Backup costs for UK South',
          description: 'Use AzureCalc.uk to estimate monthly Azure Backup costs in GBP for VM, SQL Server, and Azure Files workloads.',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Add protected instances', text: 'Add each workload (VM, SQL Server in VM, or Azure Files) with its active data size and retention policy.' },
            { '@type': 'HowToStep', position: 2, name: 'Choose storage redundancy', text: 'Select LRS, ZRS, GRS, or RA-GRS for your backup vault. GRS/RA-GRS roughly double the storage cost.' },
            { '@type': 'HowToStep', position: 3, name: 'Review the breakdown', text: 'The calculator shows instance fees vs vault storage cost — storage typically dominates at 60–80% of the total.' },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([
          { name: 'Home', url: '/', position: 1 },
          { name: 'Azure Backup Calculator', url: '/azure-backup', position: 2 },
        ]))}</script>
      </Helmet>

      <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
        <Breadcrumbs />
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Azure Backup Cost Calculator</h1>
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>UK South pricing · VM, SQL Server, Azure Files · LRS / GRS / ZRS / RA-GRS vault storage · Storage-dominates breakdown</p>
      </div>

      <AzureBackupCalc />

      <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>Learn more</h2>
        <Link
          to="/guides/azure-backup-pricing-uk"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Backup Pricing UK 2026</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Why vault storage dominates, LRS vs GRS tradeoffs, and long-term retention cost impact</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
